<template>
  <a-form-item
    :label-col="{ span: labelColSize }"
    :wrapper-col="{ span: wrapperColSize }"
    :label="label"
    :validate-status="error ? 'error' : ''"
    :help="error"
  >
    <a-select
      :value="value"
      :mode="mode"
      :placeholder="placeholder"
      :allow-clear="allowClear"
      class="w-100"
      show-search
      :filter-option="applyFuzzyFilter"
      :disabled="disabled"
      @change="handleChange"
    >
      <template v-for="option in searchCompatibleOptions">
        <a-select-opt-group
          v-if="isGroupOption(option)"
          :key="`group-${option.label}`"
          :label="option.label"
        >
          <a-select-option
            v-for="groupOption in option.options"
            :key="String(getOptionValue(groupOption))"
            :value="getOptionValue(groupOption)"
            :disabled="groupOption.disabled"
          >
            {{ groupOption.label }}
          </a-select-option>
        </a-select-opt-group>

        <a-select-option
          v-else
          :key="String(getOptionValue(option))"
          :value="getOptionValue(option)"
          :disabled="option.disabled"
        >
          {{ option.label }}
        </a-select-option>
      </template>
    </a-select>
  </a-form-item>
</template>

<script setup lang="ts">
import { ref, watch } from "vue";

interface SelectLeafOption {
  label: string;
  key?: number | string;
  value?: number | string;
  disabled?: boolean;
}

interface SelectGroupOption {
  label: string;
  options: SelectLeafOption[];
}

type SearchElement = SelectLeafOption | SelectGroupOption;

const props = defineProps({
  value: { type: [Number, Array], default: undefined },
  label: { type: String, default: "" },
  error: { type: String, default: "" },
  placeholder: { type: String, default: "" },
  labelColSize: { type: Number, default: 8 },
  wrapperColSize: { type: Number, default: 16 },
  allowClear: { type: Boolean, default: false },
  mode: { type: String, default: "" },
  options: { type: Array as () => Array<Record<string, any>>, default: () => [] },
  optionAttributeName: { type: String, default: "name" },
  disabled: { type: Boolean, default: false },
});

const emit = defineEmits(["input", "change"]);

const handleChange = (id: string): void => {
  emit("input", id);
  emit("change", id);
};

//Data properties
const searchCompatibleOptions = ref<SearchElement[]>([]);

const applyFuzzyFilter = (inputValue: string, option: any): boolean => {
  const inputValueSimplified = removeDiacritics(inputValue.toLowerCase());
  const optionLabel = getOptionLabel(option);

  if (optionLabel) {
    const optionLabelSimplified = removeDiacritics(optionLabel.toLowerCase());
    return (
      optionLabel.toLowerCase().includes(inputValueSimplified) ||
      optionLabelSimplified.includes(inputValueSimplified)
    );
  }

  return false;
};

const updateSearchCompatibleList = (list: Array<Record<string, any>>): void => {
  searchCompatibleOptions.value = list.map((elem: Record<string, any>) => normalizeOption(elem));
};

const normalizeOption = (elem: Record<string, any>): SearchElement => {
  if (Array.isArray(elem.options)) {
    return {
      label: getElementLabel(elem),
      options: elem.options.map((option: Record<string, any>) => normalizeLeafOption(option)),
    };
  }

  if (Array.isArray(elem.children)) {
    return {
      label: getElementLabel(elem),
      options: elem.children.map((option: Record<string, any>) => normalizeLeafOption(option)),
    };
  }

  return normalizeLeafOption(elem);
};

const normalizeLeafOption = (elem: Record<string, any>): SelectLeafOption => {
  const label = getElementLabel(elem);
  const value = elem.value ?? elem.id ?? elem.key;

  return {
    key: value,
    value,
    label,
    disabled: Boolean(elem.disabled),
  };
};

const getElementLabel = (elem: Record<string, any>): string => {
  return String(elem.label ?? elem[props.optionAttributeName] ?? "");
};

const getOptionLabel = (option: any): string => {
  if (typeof option?.label === "string") {
    return option.label;
  }

  if (option?.componentOptions?.children?.[0]?.text) {
    return String(option.componentOptions.children[0].text);
  }

  return "";
};

const getOptionValue = (option: SelectLeafOption): number | string => {
  return option.value ?? option.key ?? option.label;
};

const isGroupOption = (option: SearchElement): option is SelectGroupOption => {
  return Array.isArray((option as SelectGroupOption).options);
};

const removeDiacritics = (val: string): string => {
  return val.normalize("NFD").replace(/[\u0300-\u036f]/g, "");
};

watch(
  () => props.options,
  (newOptions: Array<Record<string, any>>) => {
    updateSearchCompatibleList(newOptions);
  },
  { immediate: true }
);
</script>
